import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { useI18n } from '../i18n/I18nProvider';
import { IconButton } from './Button';
import { cx } from './cx';
import styles from './Sheet.module.css';

export interface SheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Becomes the dialog's accessible name (Heading slot="title"). */
  title: ReactNode;
  children: ReactNode;
  /**
   * `end`: slides in from the inline end (34rem, full height; full screen below 40rem), so the page
   * stays visible beside it. `bottom`: rises from the bottom edge (phones).
   */
  placement?: 'end' | 'bottom';
  /** Sticky footer under the scrolling body (the sheet's own actions). */
  footer?: ReactNode;
  /** Shown before the title, e.g. a way back to the sheet's first view. */
  headerStart?: ReactNode;
  /** data-testid of the dialog element. */
  testId?: string;
  isDismissable?: boolean;
  className?: string;
}

/**
 * Modal sheet (DESIGN_SYSTEM §6, §6a): React Aria ModalOverlay + Modal + Dialog. Focus is trapped
 * inside and restored to the trigger on close; Escape and a press outside close it.
 */
export function Sheet({
  isOpen,
  onOpenChange,
  title,
  children,
  placement = 'end',
  footer,
  headerStart,
  testId,
  isDismissable = true,
  className,
}: SheetProps) {
  const { t } = useI18n();
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      className={cx(styles.overlay, placement === 'bottom' ? styles.atBottom : styles.atEnd)}
    >
      <Modal className={cx(styles.sheet, placement === 'bottom' ? styles.bottom : styles.end)}>
        <Dialog className={cx(styles.dialog, className)} data-testid={testId}>
          <div className={styles.header}>
            {headerStart}
            <Heading slot="title" className={styles.title}>
              {title}
            </Heading>
            <IconButton
              label={t.common.dismiss}
              icon={<X size={18} />}
              size="sm"
              className={styles.close}
              onPress={() => onOpenChange(false)}
            />
          </div>
          <div className={styles.body}>{children}</div>
          {footer ? <div className={styles.footer}>{footer}</div> : null}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
